// @flow
import * as React from 'react';
import styled from 'styled-components';

import type { Element, ElementProps } from 'react';

import theme from '@theme';

import type {
  PropsSVGImageFixedAspectRatio,
  PropsSvgImageWithSlope,
  PropsSvgImageWithoutSlope
} from './types';

const { colors } = theme;

const strokeWidth = 4;
const markerWidth = 5;
const markerHeight = 5;
const markerOffset = strokeWidth / 2;

const points = `
  ${markerOffset},${markerOffset}
  ${markerWidth + markerOffset},${markerHeight / 2 + markerOffset}
  ${markerOffset},${markerHeight + markerOffset}
`;

export const SVG = ({ children, ...restProps }: ElementProps<'svg'>): Element<'svg'> => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="100%"
    height="100%"
    version="1.1"
    baseProfile="full"
    preserveAspectRatio="none"
    {...restProps}
  >
    <defs>
      <marker
        id="MedimakrDefaultSvgMarker"
        markerWidth={markerWidth + strokeWidth}
        markerHeight={markerHeight + strokeWidth}
        refX={markerWidth - markerOffset}
        refY={markerHeight / 2 + markerOffset}
        orient="auto"
        markerUnits="strokeWidth"
      >
        <polyline points={points} fill="none" stroke={colors.lightBlue} strokeWidth="1" />
      </marker>
    </defs>
    {children}
  </svg>
);

export const PolyLine = ({
  hideMarker,
  ...restProps
}: ElementProps<'polyline'>): Element<'polyline'> => (
  <polyline
    strokeWidth="3"
    stroke={colors.lightBlue}
    strokeDasharray="5"
    fill="none"
    markerEnd={hideMarker ? 'none' : 'url(#MedimakrDefaultSvgMarker)'}
    {...restProps}
  />
);

export const SVGImageFixedAspectRatio = React.memo<PropsSVGImageFixedAspectRatio>(
  ({
    imageWidth, imageHeight, imageUrl, imageAlt, slopeVersion = '1'
  }: PropsSVGImageFixedAspectRatio) => {
    const id = `SVGImageFixedAspectRatio_${Buffer.from(imageUrl).toString('base64')}`;
    const width = parseInt(imageWidth, 10);
    const height = parseInt(imageHeight, 10);
    const Wrapper = styled.div`
      width: 100%;
      padding-top: ${(height / width) * 100}%;
      position: relative;
    `;
    const StyledSVG = styled(({ ...rest }) => <SVG {...rest} />)`
      position: absolute;
      left: 0;
      top: 0;
      border-radius: 6px;

      path {
        display: none;
      }

      path:nth-of-type(${slopeVersion}) {
        display: block;
      }
    `;
    const slopeOffset = Math.round(height * 0.9);

    return (
      <Wrapper>
        <StyledSVG
          slopeVersion={slopeVersion}
          viewBox={`0, 0, ${width}, ${height}`}
          preserveAspectRatio="none"
        >
          <path d={`M0,0 L${width},0 L${width},${height} L0,${height} L0,0`} fill={`url(#${id})`} />
          <path
            d={`M0,0 L${width},0 L${width},${slopeOffset} L0,${height} L0,0`}
            fill={`url(#${id})`}
          />
          <path
            d={`M${width * 0.1},0 L${width},0 L${width
              - width * 0.1},${height} L0,${height} L${width * 0.117},0`}
            fill={`url(#${id})`}
          />
          <path
            d={`M0,100 L${width},0 L${width},${height} L0,${height} L0,100`}
            fill={`url(#${id})`}
          />
          <path
            d={`M0,0 L${width},0 L${width - width * 0.1},${height} L0,${height} L0,0`}
            fill={`url(#${id})`}
          />
          <path
            d={`M0,0 L${width},100 L${width},${height} L0,${height} L0,0`}
            fill={`url(#${id})`}
          />
          <desc>{imageAlt}</desc>
          <defs>
            <pattern id={id} patternUnits="userSpaceOnUse" width={width} height={height}>
              <image
                xlinkHref={imageUrl}
                x="0"
                y="0"
                width={width}
                height={height}
                preserveAspectRatio="xMidYMid slice"
              />
            </pattern>
          </defs>
        </StyledSVG>
      </Wrapper>
    );
  }
);

export const SVGImageWithSlope = ({
  width,
  height,
  offsetSlope,
  imageUrl,
  left = false,
  styles
}: PropsSvgImageWithSlope) => {
  const id = `SVGImageWithSlope_${Buffer.from(imageUrl).toString('base64')}`;
  const StyledSVG = styled(SVG)`
    ${styles}
  `;

  const pathSlopeLeft = `
    M${offsetSlope},0
    L${width},0
    L${width},${height}
    L0,${height}
    L${offsetSlope},0
  `;

  const pathSlopeRight = `
    M0,0
    L${width},0
    L${width - offsetSlope},${height}
    L0,${height}
    L0,${offsetSlope}
  `;

  return (
    <StyledSVG
      width="62%"
      height="100%"
      viewBox={`0, 0, ${width}, ${height}`}
      preserveAspectRatio="none"
    >
      <path d={left ? pathSlopeLeft : pathSlopeRight} fill={`url(#${id})`} />
      <defs>
        <pattern id={id} patternUnits="userSpaceOnUse" width={width} height={height}>
          <image
            xlinkHref={imageUrl}
            x="0"
            y="0"
            width={width}
            height={height}
            preserveAspectRatio="xMidYMid slice"
          />
        </pattern>
      </defs>
    </StyledSVG>
  );
};

export const SVGImageWithoutSlope = ({
  width,
  height,
  imageUrl,
  styles
}: PropsSvgImageWithoutSlope) => {
  // const id = `SVGImageWithSlope_${Buffer.from(imageUrl).toString('base64')}`;
  const StyledSVG = styled(SVG)`
    ${styles}
  `;

  return (
    <StyledSVG
      width="100%"
      height="100%"
      viewBox={`0, 0, ${width}, ${height}`}
      preserveAspectRatio="none"
    >
      <image xlinkHref={imageUrl} x="0" y="0" width={width} height={height} />
    </StyledSVG>
  );
};

export const SVGSlopedMask = ({ left = false, ...restProps }: ElementProps<'svg'>) => {
  const width = 1;
  const height = 1;

  const pathSlopeRight = `
    M1,1
    L${width},1
    L${width},${height}
    L0,${height}
    L${width},0
  `;

  const pathSlopeLeft = `
    M0,0
    L${width},0
    L0,${height}
    L0,${height}
    L0,0
  `;

  return (
    <svg
      width="62%"
      height="100%"
      viewBox={`0, 0, ${width}, ${height}`}
      preserveAspectRatio="none"
      {...restProps}
    >
      <path d={left ? pathSlopeLeft : pathSlopeRight} fill="#FFFFFF" />
    </svg>
  );
};
